import { useState, useEffect } from "react";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import { Icon } from "@iconify/react";
import {
  addDays,
  format,
  getMonth,
  getYear,
  eachWeekOfInterval,
  endOfMonth,
  startOfMonth,
  endOfYear,
  startOfYear,
  getWeek,
  isSameDay,
} from "date-fns";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../helper/urls";
import axios from "axios";
import moment from "moment-timezone";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initialSchedule = [
  {
    day: "Monday",
    dayLetter: "Mon",
    isAvailable: true,
    timeSlots: [],
  },
  {
    day: "Tuesday",
    dayLetter: "Tue",
    isAvailable: true,
    timeSlots: [],
  },
  {
    day: "Wednesday",
    dayLetter: "Wed",
    isAvailable: true,
    timeSlots: [],
  },
  {
    day: "Thursday",
    dayLetter: "Thu",
    isAvailable: true,
    timeSlots: [],
  },
  {
    day: "Friday",
    dayLetter: "Fri",
    isAvailable: true,
    timeSlots: [],
  },
  {
    day: "Saturday",
    dayLetter: "Sat",
    isAvailable: true,
    timeSlots: [],
  },
  {
    day: "Sunday",
    dayLetter: "Sun",
    isAvailable: true,
    timeSlots: [],
  },
];

const WeekCalendar = () => {
  const { token, interestedIn } = useSelector((state) => state.auth);
  const [selectedWeek, setSelectedWeek] = useState({ start: null, end: null });
  const [showModal, setShowModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(getMonth(new Date()));
  const [schedule, setSchedule] = useState(initialSchedule);
  const [isLoading, setIsLoading] = useState(false);
  const [existingSlots, setExistingSlots] = useState([]);
  const [weekDetails, setWeekDetails] = useState([]);

  // Helper function to convert 12-hour format to 24-hour format
  const convertTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");
    if (hours === "12") {
      hours = "00";
    }
    if (modifier === "PM") {
      hours = Number.parseInt(hours, 10) + 12;
    }
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // Helper function to convert 24-hour format to 12-hour format
  const convertTo12Hour = (time24h) => {
    const [hours, minutes] = time24h.split(":");
    const hour = Number.parseInt(hours, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  // Helper function to get week number for a specific date
  const getWeekNumber = (date) => {
    return getWeek(date, { weekStartsOn: 1 }); // Monday as start of week
  };

  // Helper function to get date for a specific day in the selected week
  const getDateForDay = (dayIndex) => {
    if (!selectedWeek.start) return null;
    return addDays(selectedWeek.start, dayIndex);
  };

  // Helper function to get user's timezone
  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Helper function to convert local time to UTC
  const convertToUTC = (date, time24h, timezone) => {
    const [hours, minutes] = time24h.split(":");

    // Create moment object in the given timezone
    const localMoment = moment.tz(
      `${format(date, "yyyy-MM-dd")} ${time24h}`,
      "YYYY-MM-DD HH:mm",
      timezone
    );

    // Convert to UTC and return ISO 8601 string with "Z"
    return localMoment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
  };

  // Helper function to check if a date is in the past or current date
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate <= today;
  };

  // Helper function to check if a week contains any future dates
  const hasAnyFutureDate = (weekStart, weekEnd) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const endDate = new Date(weekEnd);
    endDate.setHours(0, 0, 0, 0);
    return endDate >= tomorrow;
  };

  // Helper function to check if a day in the modal should be disabled
  const isDayDisabled = (dayIndex) => {
    const dayDate = getDateForDay(dayIndex);
    return dayDate ? isPastDate(dayDate) : true;
  };
  // Fetch Week Details
  const fetchWeekDetails = async () => {
    try {
      const response = await axios.get(`${baseUrl}/slots/getWeekDetails`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setWeekDetails(response.data);
    } catch (error) {
      console.error("Error fetching week details:", error);
      toast.error("Failed to fetch week details");
      return null;
    }
  };

  useEffect(() => {
    fetchWeekDetails();
  }, []);

  const getSlotDataForWeek = (weekNumber) => {
    const weekKey = `week_no_${weekNumber}`;
    const foundWeek = weekDetails.find((week) => week[weekKey]);
    return foundWeek ? foundWeek[weekKey] : null;
  };

  // Fetch existing slots
  const fetchSlots = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/slots/counselor/counselorId`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setExistingSlots(response.data?.data || []);
      return response.data?.data || [];
    } catch (error) {
      console.error("Error fetching slot list:", error);
      toast.error("Failed to fetch existing slots");
      setExistingSlots([]);
      return [];
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  // Function to delete a slot
  const deleteSlot = async (slotId) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/slots/destory/${slotId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Delete Success:", response.data);
      // Refresh slots after deletion
      await fetchSlots();
      // Reload the modal with updated data
      if (selectedWeek.start) {
        loadExistingSlots();
      }
      toast.success("Slot deleted successfully!");
    } catch (error) {
      console.error("Delete Failed:", error.response?.data || error.message);
      toast.error("Failed to delete slot");
    }
  };

  // Load existing slots for the selected week
  const loadExistingSlots = () => {
    if (!selectedWeek.start || !existingSlots || existingSlots.length === 0) {
      setSchedule(initialSchedule);
      return;
    }

    const updatedSchedule = [...initialSchedule];

    // Add null check when iterating through existingSlots
    existingSlots.forEach((slot) => {
      if (!slot) return;
      console.log("slotDate............" + slot.date);
      // const temp= slot.date
      const slotDate = new Date(slot.utcTime);
      // slotDate.setHours(12);
      // new Date(millisecondsSince1970)
      console.log("slotDate" + slotDate);

      // Find which day of the week this slot belongs to
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        console.log("dayIndex" + dayIndex);
        const dayDate = getDateForDay(dayIndex);
        console.log("dayDate" + dayDate);
        if (dayDate && isSameDay(slotDate, dayDate)) {
          const startTime12 = convertTo12Hour(slot.start_time);
          console.log("startTime12" + startTime12);
          const endTime12 = convertTo12Hour(slot.end_time);
          console.log("endTime12" + endTime12);
          const timeRange = `${startTime12} - ${endTime12}`;
          console.log("timeRange" + timeRange);
          // Add the slot to the schedule
          updatedSchedule[dayIndex] = {
            ...updatedSchedule[dayIndex],
            isAvailable: true,
            timeSlots: [
              ...(updatedSchedule[dayIndex].timeSlots || []),
              {
                id: slot.id, // Use the actual slot ID from API
                timeRange: timeRange,
                isExisting: true,
                isBooked: slot.is_available === 0,
                slotData: slot,
              },
            ],
          };
          break;
        }
      }
    });

    setSchedule(updatedSchedule);
  };

  // Load existing slots when modal opens or week changes
  useEffect(() => {
    if (showModal && selectedWeek.start) {
      loadExistingSlots();
    }
  }, [showModal, selectedWeek, existingSlots]);

  const prepareEssaySlots = () => {
    const slots = [];
    const timezone = getUserTimezone(); // E.g., 'Asia/Kolkata'

    schedule.forEach((day, dayIndex) => {
      if (day.isAvailable && day.timeSlots.length > 0) {
        const dayDate = getDateForDay(dayIndex); // This should return a JavaScript Date object
        if (dayDate) {
          day.timeSlots.forEach((slot) => {
            // if (slot.timeRange && !slot.isExisting) {

            if (slot.timeRange) {
              const [startTime, endTime] = slot.timeRange.split(" - ");
              const startTime24 = convertTo24Hour(startTime);
              const endTime24 = convertTo24Hour(endTime);

              const dateKey = moment(dayDate).format("YYYY-MM-DD");
              const localStart = moment.tz(
                `${dateKey} ${startTime24}`,
                "YYYY-MM-DD HH:mm",
                timezone
              );
              const utcTime = localStart.utc().toISOString();

              const weekNo = getWeekNumber(dayDate);

              slots.push({
                date: dateKey,
                start_time: startTime24,
                end_time: endTime24,
                week_no: weekNo,
                utcTime: utcTime,
                id: slot?.slotData?.id || null,
              });
            }
          });
        }
      }
    });

    return slots;
  };

  // Prepare slots data for Counselling API (grouped by date)
  const prepareCounsellingSlots = () => {
    const slotsByDate = {};
    const timezone = getUserTimezone();
    console.log("schedule:::::::::::::;", schedule);
    schedule.forEach((day, dayIndex) => {
      if (day.isAvailable && day.timeSlots.length > 0) {
        const dayDate = getDateForDay(dayIndex);
        if (dayDate) {
          const dateStr = format(dayDate, "yyyy-MM-dd");
          console.log("dateStr:::::::::::::;", dateStr);

          day.timeSlots.forEach((slot) => {
            // Only include new slots (not existing ones)
            if (slot.timeRange) {
              // if (slot.timeRange && !slot.isExisting) {
              if (!slotsByDate[dateStr]) {
                slotsByDate[dateStr] = [];
              }

              const [startTime, endTime] = slot.timeRange.split(" - ");
              const startTime24 = convertTo24Hour(startTime);
              const endTime24 = convertTo24Hour(endTime);
              const weekNo = getWeekNumber(dayDate);
              const utcTime = convertToUTC(dayDate, startTime24, timezone);

              slotsByDate[dateStr].push({
                start_time: startTime24,
                end_time: endTime24,
                week_no: weekNo,
                utcTime: utcTime,
                id: slot?.slotData?.id || null,
                date: dateStr,
              });
            }
          });
        }
      }
    });

    return slotsByDate;
  };

  // API call for Essay Counseling
  const createEssaySlots = async (slots) => {
    const timezone = getUserTimezone();
    const requestData = {
      type: interestedIn,
      time_zone: timezone,
      slots: slots,
    };

    try {
      const response = await axios.post(
        `${baseUrl}/slots/createSlotForEssay`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Essay slots created:", response.data);
      toast.success("Slots created successfully!");
      return true;
    } catch (error) {
      console.error(
        "Error creating slots:",
        error.response?.data || error.message
      );
      toast.error("Failed to create slots.");
      return false;
    }
  };

  // API call for College Counseling
  const createCounsellingSlots = async (slotsByDate) => {
    const timezone = getUserTimezone();

    try {
      const promises = Object.entries(slotsByDate).map(([date, slots]) => {
        const requestData = {
          date: date,
          type: interestedIn,
          time_zone: timezone,
          slots: slots,
        };

        return axios.post(`${baseUrl}/slots`, requestData, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      });

      await Promise.all(promises);
      toast.success("Slots created successfully!");
      return true;
    } catch (error) {
      console.error(
        "Error creating counselling slots:",
        error.response?.data || error.message
      );
      toast.error("Failed to create counselling slots.");
      return false;
    }
  };

  // Handle save button click
  const handleSave = async () => {
    setIsLoading(true);

    try {
      // if (
      //   interestedIn === "Essay Counseling" ||
      //   interestedIn === "Essay + College Counseling"
      // ) {
      if (true) {
        const essaySlots = prepareEssaySlots();
        console.log("essaySlots:::::::::::::::", essaySlots);

        const success = await createEssaySlots(essaySlots);
        if (success) {
          await fetchSlots(); // Refresh slots
          setShowModal(false);
        }
      } else if (interestedIn === "College Counseling") {
        const counsellingSlots = prepareCounsellingSlots();

        const success = await createCounsellingSlots(counsellingSlots);
        if (success) {
          await fetchSlots(); // Refresh slots
          setShowModal(false);
        }
      } else {
        toast.warn("Please select a valid service type");
      }
    } catch (error) {
      console.error("Error in handleSave:", error);
      toast.error("An error occurred while saving slots");
    } finally {
      setIsLoading(false);
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getWeeks = () => {
    let startDate, endDate;

    if (selectedMonth === "") {
      startDate = startOfYear(new Date(selectedYear, 0, 1));
      endDate = endOfYear(startDate);
    } else {
      startDate = startOfMonth(new Date(selectedYear, selectedMonth, 1));
      endDate = endOfMonth(startDate);
    }

    const weekStarts = eachWeekOfInterval(
      { start: startDate, end: endDate },
      { weekStartsOn: 1 }
    );

    return weekStarts
      .map((weekStart) => ({
        weekStart,
        weekEnd: addDays(weekStart, 6),
        weekNumber: getWeekNumber(weekStart),
      }))
      .filter(({ weekStart, weekEnd }) => hasAnyFutureDate(weekStart, weekEnd));
  };

  const weeks = getWeeks();

  const handleWeekClick = (weekStart) => {
    const weekEnd = addDays(weekStart, 6);

    // Check if the week has any future dates
    if (!hasAnyFutureDate(weekStart, weekEnd)) {
      return; // Don't open modal for past weeks
    }

    setSelectedWeek({ start: weekStart, end: weekEnd });
    setShowModal(true);
  };

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const toggleDayAvailability = (dayIndex) => {
    setSchedule((prev) =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              isAvailable: !day.isAvailable,
              timeSlots: !day.isAvailable
                ? [{ id: generateId(), timeRange: timeRangeOptions[0] }]
                : (day.timeSlots || []).filter((slot) => slot.isExisting), // Keep existing slots with null check
            }
          : day
      )
    );
  };

  const addTimeSlot = (dayIndex) => {
    const selectedRanges = getSelectedTimeRanges(dayIndex);
    const availableRange = timeRangeOptions.find(
      (range) => !selectedRanges.includes(range)
    );

    setSchedule((prev) =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              timeSlots: [
                ...day.timeSlots,
                {
                  id: generateId(),
                  timeRange: availableRange || "",
                },
              ],
            }
          : day
      )
    );
  };

  const removeTimeSlot = (dayIndex, slotId, isExisting = false) => {
    if (isExisting) {
      // Call delete API for existing slots
      if (window.confirm("Are you sure you want to delete this slot?")) {
        deleteSlot(slotId);
      }
    } else {
      // Remove from local state for new slots
      setSchedule((prev) =>
        prev.map((day, index) =>
          index === dayIndex
            ? {
                ...day,
                timeSlots: day.timeSlots.filter((slot) => slot.id !== slotId),
              }
            : day
        )
      );
    }
  };

  const updateTimeSlot = (dayIndex, slotId, value) => {
    console.log(dayIndex, slotId, value, "ddddddddddddd");
    setSchedule((prev) =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              timeSlots: day.timeSlots.map((slot) =>
                slot.id === slotId ? { ...slot, timeRange: value } : slot
              ),
            }
          : day
      )
    );
  };

  const getSelectedTimeRanges = (dayIndex) => {
    const day = schedule[dayIndex];
    if (!day || !day.timeSlots) return [];

    return day.timeSlots
      .filter((slot) => slot.timeRange && slot.timeRange !== "")
      .map((slot) => slot.timeRange);
  };

  const canAddMoreSlots = (dayIndex) => {
    const selectedRanges = getSelectedTimeRanges(dayIndex);
    return selectedRanges.length < timeRangeOptions.length;
  };

  const generateTimeRanges = () => {
    const ranges = [];
    for (let hour = 8; hour < 20; hour++) {
      const startHour = hour % 12 === 0 ? 12 : hour % 12;
      const endHour = (hour + 1) % 12 === 0 ? 12 : (hour + 1) % 12;
      const startPeriod = hour < 12 ? "AM" : "PM";
      const endPeriod = hour + 1 < 12 ? "AM" : "PM";
      ranges.push(
        `${startHour}:00 ${startPeriod} - ${endHour}:00 ${endPeriod}`
      );
    }
    return ranges;
  };

  const timeRangeOptions = generateTimeRanges();

  const styles = {
    container: {
      margin: "2rem auto",
    },
    header: {
      display: "flex",
      flex: "wrap",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1rem",
    },
    heading: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      marginBottom: "0px",
    },

    calendarGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: "1rem",
      justifyContent: "start",
    },

    weekCell: {
      width: "334px",
      height: "108px",
      backgroundColor: "white",
      padding: "20px",
      borderRadius: "10px",
      cursor: "pointer",
      boxShadow: "0 0 8px rgba(0, 0, 0, 0.05)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      boxSizing: "border-box",
    },
  };

  return (
    <div style={styles.container}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="row align-items-center justify-content-between mb-3">
        <div className="flex justify-center items-center text-center">
          <h6 className="justify-content-center text-xlg text-warning-600">
            <b>
              Please add atleast 1 slot to be visible to students for booking.
            </b>{" "}
          </h6>
        </div>
        <div className="flex justify-center items-center text-center">
          <span
            style={{
              height: "15px",
              width: "15px",
              backgroundColor: "green",
              borderRadius: "50%",
              display: "inline-block",
              marginRight: "5px",
            }}
          ></span>
          <span
            style={{
              marginRight: "5px",
            }}
          >
            Available Slots
          </span>

          <span
            style={{
              height: "15px",
              width: "15px",
              backgroundColor: "red",
              borderRadius: "50%",
              display: "inline-block",
              marginRight: "5px",
            }}
          ></span>
          <span>Booked Slots</span>
        </div>

        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <p style={styles.heading}>Weekly Calendar</p>
        </div>
        <div className="col-12 col-md-6 d-flex flex-wrap gap-2 justify-content-md-end">
          {/* Month Dropdown */}
          <div className="dropdown">
            <button
              className="btn btn-outline-primary-600 not-active px-18 py-11 dropdown-toggle toggle-icon"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {selectedMonth === "" ? "All Months" : months[selectedMonth]}
            </button>
            <ul className="dropdown-menu">
              {months.map((month, idx) => {
                const isDisabled =
                  selectedYear === getYear(new Date()) &&
                  idx < getMonth(new Date());

                return (
                  <li key={idx}>
                    <button
                      className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${
                        isDisabled ? "disabled text-muted" : "text-primary"
                      }`}
                      onClick={() => {
                        if (!isDisabled) setSelectedMonth(idx);
                      }}
                      disabled={isDisabled}
                    >
                      {month}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      <div>
        <div style={styles.calendarGrid}>
          {weeks.map(({ weekStart, weekEnd, weekNumber }, i) => {
            const isWeekDisabled = !hasAnyFutureDate(weekStart, weekEnd);

            return (
              <div
                key={i}
                style={{
                  ...styles.weekCell,
                  opacity: isWeekDisabled ? 0.5 : 1,
                  cursor: isWeekDisabled ? "not-allowed" : "pointer",
                  border: !isWeekDisabled
                    ? "2px solid #28a745"
                    : "1px solid #dee2e6",
                }}
                onClick={() => !isWeekDisabled && handleWeekClick(weekStart)}
              >
                <div>
                  <div>
                    {format(weekStart, "MMM dd")} - {format(weekEnd, "MMM dd")}
                  </div>
                  <small className="text-muted">Week {weekNumber}</small>
                </div>
                <div>
                  {(() => {
                    const slotData = getSlotDataForWeek(weekNumber);
                    if (!slotData) return null;

                    const { totolNoOfSlot, avaliableNoOfSlot, bookedOfSlot } =
                      slotData;
                    const showGreenOnly =
                      avaliableNoOfSlot <= totolNoOfSlot &&
                      avaliableNoOfSlot > 0;
                    const showRedOnly =
                      bookedOfSlot === totolNoOfSlot && totolNoOfSlot > 0;
                    const showBoth =
                      bookedOfSlot < totolNoOfSlot &&
                      totolNoOfSlot > 0 &&
                      bookedOfSlot > 0;

                    return (
                      <>
                        {showGreenOnly && (
                          <span
                            style={{
                              height: "15px",
                              width: "15px",
                              backgroundColor: "green",
                              borderRadius: "50%",
                              display: "inline-block",
                              marginRight: "5px",
                            }}
                          ></span>
                        )}
                        {showBoth && (
                          <>
                            <span
                              style={{
                                height: "15px",
                                width: "15px",
                                backgroundColor: "red",
                                borderRadius: "50%",
                                display: "inline-block",
                              }}
                            ></span>
                          </>
                        )}
                        {showRedOnly && (
                          <span
                            style={{
                              height: "15px",
                              width: "15px",
                              backgroundColor: "red",
                              borderRadius: "50%",
                              display: "inline-block",
                            }}
                          ></span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex" }}>
            <Icon icon="mdi:calendar-clock" className="me-2" />
            {selectedWeek.start && selectedWeek.end && (
              <p
                className="text-muted"
                style={{
                  marginBottom: "0px",
                  fontSize: "21px",
                  fontWeight: "600",
                  marginLeft: "10px",
                }}
              >
                {format(selectedWeek.start, "MMM dd, yyyy")} - {""}
                {format(selectedWeek.end, "MMM dd, yyyy")}
              </p>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* {selectedWeek.start && selectedWeek.end && (
            <div className="mb-3">
              <p className="text-danger small">
                Service: {interestedIn} | Minimum slots required:{" "}
                {interestedIn === "Essay Counseling" ||
                interestedIn === "Essay + College Counseling"
                  ? "5"
                  : "1"}
              </p>
            </div>
          )} */}
          {schedule.map((day, dayIndex) => {
            const dayDate = getDateForDay(dayIndex);
            const isDisabled = isDayDisabled(dayIndex);
            const isFutureDay = dayDate && !isPastDate(dayDate);

            return (
              <div key={day.day} className="mb-3">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <Badge
                    bg={
                      isDisabled
                        ? "secondary"
                        : day.isAvailable
                        ? "primary"
                        : "secondary"
                    }
                    style={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      width: "2rem",
                      height: "2rem",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "12px",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                    className="rounded-circle p-2"
                    onClick={() =>
                      !isDisabled && toggleDayAvailability(dayIndex)
                    }
                  >
                    {day.dayLetter}
                  </Badge>

                  {isDisabled ? (
                    <span className="text-muted">Past Date</span>
                  ) : !day.isAvailable ? (
                    <>
                      <span className="text-muted">Unavailable</span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        onClick={() => toggleDayAvailability(dayIndex)}
                      >
                        <Icon icon="mdi:plus" />
                      </Button>
                    </>
                  ) : (
                    <div className="flex-grow-1">
                      {/* Add null check for timeSlots */}
                      {(day.timeSlots || []).map((slot) => (
                        <div
                          key={slot.id}
                          className="d-flex align-items-center gap-2 mb-2"
                        >
                          <Form.Select
                            value={slot.timeRange || ""}
                            onChange={(e) =>
                              updateTimeSlot(dayIndex, slot.id, e.target.value)
                            }
                            style={{
                              width: "200px",
                              height: "36px",
                              padding: "5px",
                              backgroundColor: slot.isBooked
                                ? "#f8f9fa"
                                : "white",
                            }}
                            disabled={isDisabled || slot.isBooked}
                          >
                            <option value="">Select time range</option>
                            {timeRangeOptions.map((range) => {
                              const selectedRanges =
                                getSelectedTimeRanges(dayIndex);
                              const isAlreadySelected =
                                selectedRanges.includes(range) &&
                                slot.timeRange !== range;

                              return (
                                <option
                                  key={range}
                                  value={range}
                                  disabled={isAlreadySelected}
                                  style={{
                                    color: isAlreadySelected
                                      ? "#6c757d"
                                      : "inherit",
                                    backgroundColor: isAlreadySelected
                                      ? "#f8f9fa"
                                      : "inherit",
                                  }}
                                >
                                  {range}{" "}
                                  {isAlreadySelected
                                    ? "(Already Selected)"
                                    : ""}
                                </option>
                              );
                            })}
                          </Form.Select>

                          <button
                            size="sm"
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "none",
                            }}
                            onClick={() => addTimeSlot(dayIndex)}
                            disabled={isDisabled || !canAddMoreSlots(dayIndex)}
                          >
                            <Icon icon="mdi:plus" />
                          </button>

                          {/* Show delete button for existing slots when is_available = 0 or for new slots */}
                          {(slot.isExisting && !slot.isBooked) ||
                          !slot.isExisting ? (
                            <button
                              size="sm"
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                border: "none",
                              }}
                              onClick={() =>
                                removeTimeSlot(
                                  dayIndex,
                                  slot.id,
                                  slot.isExisting
                                )
                              }
                              disabled={isDisabled}
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          ) : null}

                          {slot.isExisting && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <p
                                className="ms-2"
                                style={{
                                  color: slot.isBooked ? "red" : "green",
                                  margin: "0px",
                                }}
                              >
                                {slot.isBooked ? "Booked" : ""}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}

                      {(!day.timeSlots || day.timeSlots.length === 0) && (
                        <button
                          size="sm"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          onClick={() => addTimeSlot(dayIndex)}
                          disabled={isDisabled || !canAddMoreSlots(dayIndex)}
                        >
                          <Icon icon="mdi:plus" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {dayDate && (
                  <small className="text-muted ms-5">
                    {format(dayDate, "MMM dd, yyyy")}
                    {isFutureDay && (
                      <span className="text-success ms-2">
                        {/* Add null check for timeSlots */} (
                        {
                          (day.timeSlots || []).filter(
                            (slot) => slot.isExisting
                          ).length
                        }{" "}
                        /12 ) Slot Created
                      </span>
                    )}
                  </small>
                )}
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WeekCalendar;
